import { useState, useRef, useCallback, useEffect } from "react";
import { getUserId } from "../utils/auth";

export type SaveStatus = "idle" | "saving" | "saved" | "saved-locally" | "error";

type GetDataFn = () => Record<string, unknown> | null;

interface DraftEntry {
  formId: string;
  data: Record<string, unknown>;
  savedAt: string;
  expiresAt: string;
}

export interface UseMaintenanceAutoSaveReturn {
  saveStatus: SaveStatus;
  registerDirtyForm: (formId: string, getData: GetDataFn) => void;
  unregisterForm: (formId: string) => void;
}

function buildDraftKey(route: string, userId: string): string {
  return `seweb:draft:${route}:${userId}`;
}

export function useMaintenanceAutoSave(): UseMaintenanceAutoSaveReturn {
  const [saveStatus, setSaveStatus] = useState<SaveStatus>("idle");
  const formsRef = useRef<Map<string, GetDataFn>>(new Map());

  const registerDirtyForm = useCallback((formId: string, getData: GetDataFn) => {
    formsRef.current.set(formId, getData);
  }, []);

  const unregisterForm = useCallback((formId: string) => {
    formsRef.current.delete(formId);
  }, []);

  useEffect(() => {
    const handleAutoSave = async () => {
      const forms = formsRef.current;
      if (forms.size === 0) return;

      const userId = getUserId();
      if (!userId) return;

      const route = window.location.pathname;
      const now = new Date();
      const expiresAt = new Date(now.getTime() + 24 * 60 * 60 * 1000).toISOString();

      // Collect dirty form data (AC5: skip clean forms)
      const entries: DraftEntry[] = [];
      for (const [formId, getData] of forms) {
        const data = getData();
        if (!data || Object.keys(data).length === 0) continue;
        entries.push({
          formId,
          data,
          savedAt: now.toISOString(),
          expiresAt,
        });
      }

      if (entries.length === 0) return;

      setSaveStatus("saving");

      // Save to localStorage (primary — always works)
      const key = buildDraftKey(route, userId);
      try {
        localStorage.setItem(key, JSON.stringify(entries));
      } catch {
        setSaveStatus("error");
        window.dispatchEvent(new CustomEvent("maintenance:autosave:error"));
        return;
      }

      // Best-effort backend save (fire-and-forget)
      try {
        const token = (() => {
          try {
            const raw = localStorage.getItem("seweb_auth");
            return raw ? (JSON.parse(raw).token ?? JSON.parse(raw).access_token) : null;
          } catch {
            return null;
          }
        })();

        const baseUrl = import.meta.env.VITE_API_URL || "";

        const response = await fetch(`${baseUrl}/api/v1/maintenance/drafts`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({
            route,
            data: Object.fromEntries(entries.map((e) => [e.formId, e.data])),
          }),
        });

        if (!response.ok) throw new Error(`HTTP ${response.status}`);

        setSaveStatus("saved");
        window.dispatchEvent(new CustomEvent("maintenance:autosave:done"));
      } catch {
        // Backend failed — localStorage is source of truth (AC3)
        setSaveStatus("saved-locally");
        window.dispatchEvent(new CustomEvent("maintenance:autosave:done"));
      }
    };

    window.addEventListener("maintenance:autosave", handleAutoSave);
    return () => window.removeEventListener("maintenance:autosave", handleAutoSave);
  }, []);

  return { saveStatus, registerDirtyForm, unregisterForm };
}
